import { useCallback, useContext, useEffect, useState } from 'react'

/**
 * Polling that runs only while someone shows the data. A provider keeps a count per
 * interval its consumers asked for; the query polls at the shortest one and stops
 * when the last consumer unmounts. Changing the count re-renders only the provider —
 * its context value does not depend on it.
 */
export function useSubscriberRegistry() {
  const [wanted, setWanted] = useState({})   // interval ms → consumers
  const subscribe = useCallback((ms) => {
    setWanted(w => ({ ...w, [ms]: (w[ms] ?? 0) + 1 }))
    return () => setWanted(w => {
      const n = (w[ms] ?? 1) - 1
      const next = { ...w }
      if (n > 0) next[ms] = n
      else delete next[ms]
      return next
    })
  }, [])
  const intervals = Object.keys(wanted).map(Number)
  return { subscribe, interval: intervals.length ? Math.min(...intervals) : null }
}

/** Register the calling component as a consumer of `context` at `everyMs`; returns the context value. */
export function useSubscribed(context, everyMs) {
  const ctx = useContext(context)
  const subscribe = ctx?.subscribe
  useEffect(() => (subscribe && everyMs ? subscribe(everyMs) : undefined), [subscribe, everyMs])
  return ctx
}
